import SubscriptionService from '../../services/SubscriptionService';

export const SUBSCRIPTION_ACTIONS = {
  // Plans
  FETCH_PLANS_REQUEST: 'FETCH_PLANS_REQUEST',
  FETCH_PLANS_SUCCESS: 'FETCH_PLANS_SUCCESS',
  FETCH_PLANS_FAILURE: 'FETCH_PLANS_FAILURE',
  
  // User Subscription
  FETCH_SUBSCRIPTION_REQUEST: 'FETCH_SUBSCRIPTION_REQUEST',
  FETCH_SUBSCRIPTION_SUCCESS: 'FETCH_SUBSCRIPTION_SUCCESS',
  FETCH_SUBSCRIPTION_FAILURE: 'FETCH_SUBSCRIPTION_FAILURE',
  
  // Status Check
  CHECK_STATUS_REQUEST: 'CHECK_STATUS_REQUEST',
  CHECK_STATUS_SUCCESS: 'CHECK_STATUS_SUCCESS',
  CHECK_STATUS_FAILURE: 'CHECK_STATUS_FAILURE',
  
  // Usage
  FETCH_USAGE_REQUEST: 'FETCH_USAGE_REQUEST',
  FETCH_USAGE_SUCCESS: 'FETCH_USAGE_SUCCESS',
  FETCH_USAGE_FAILURE: 'FETCH_USAGE_FAILURE',
  
  // Payments
  FETCH_PAYMENTS_REQUEST: 'FETCH_PAYMENTS_REQUEST',
  FETCH_PAYMENTS_SUCCESS: 'FETCH_PAYMENTS_SUCCESS',
  FETCH_PAYMENTS_FAILURE: 'FETCH_PAYMENTS_FAILURE',
  
  // Checkout
  CREATE_CHECKOUT_REQUEST: 'CREATE_CHECKOUT_REQUEST',
  CREATE_CHECKOUT_SUCCESS: 'CREATE_CHECKOUT_SUCCESS',
  CREATE_CHECKOUT_FAILURE: 'CREATE_CHECKOUT_FAILURE',
  
  // Portal
  CREATE_PORTAL_REQUEST: 'CREATE_PORTAL_REQUEST',
  CREATE_PORTAL_SUCCESS: 'CREATE_PORTAL_SUCCESS',
  CREATE_PORTAL_FAILURE: 'CREATE_PORTAL_FAILURE',
  
  // Cancel/Reactivate
  CANCEL_SUBSCRIPTION_REQUEST: 'CANCEL_SUBSCRIPTION_REQUEST',
  CANCEL_SUBSCRIPTION_SUCCESS: 'CANCEL_SUBSCRIPTION_SUCCESS',
  CANCEL_SUBSCRIPTION_FAILURE: 'CANCEL_SUBSCRIPTION_FAILURE',
  
  REACTIVATE_SUBSCRIPTION_REQUEST: 'REACTIVATE_SUBSCRIPTION_REQUEST',
  REACTIVATE_SUBSCRIPTION_SUCCESS: 'REACTIVATE_SUBSCRIPTION_SUCCESS',
  REACTIVATE_SUBSCRIPTION_FAILURE: 'REACTIVATE_SUBSCRIPTION_FAILURE',
  
  // Clear errors
  CLEAR_SUBSCRIPTION_ERROR: 'CLEAR_SUBSCRIPTION_ERROR',
  
  // Admin Plan Management
  CREATE_PLAN_REQUEST: 'CREATE_PLAN_REQUEST',
  CREATE_PLAN_SUCCESS: 'CREATE_PLAN_SUCCESS',
  CREATE_PLAN_FAILURE: 'CREATE_PLAN_FAILURE',
  
  UPDATE_PLAN_REQUEST: 'UPDATE_PLAN_REQUEST',
  UPDATE_PLAN_SUCCESS: 'UPDATE_PLAN_SUCCESS',
  UPDATE_PLAN_FAILURE: 'UPDATE_PLAN_FAILURE',
  
  DELETE_PLAN_REQUEST: 'DELETE_PLAN_REQUEST',
  DELETE_PLAN_SUCCESS: 'DELETE_PLAN_SUCCESS',
  DELETE_PLAN_FAILURE: 'DELETE_PLAN_FAILURE'
};

// Action Creators

// Fetch all subscription plans
export const fetchPlans = () => async (dispatch) => {
  dispatch({ type: SUBSCRIPTION_ACTIONS.FETCH_PLANS_REQUEST });
  
  try {
    const response = await SubscriptionService.getPlans();
    dispatch({
      type: SUBSCRIPTION_ACTIONS.FETCH_PLANS_SUCCESS,
      payload: response.plans
    });
    return response.plans;
  } catch (error) {
    dispatch({
      type: SUBSCRIPTION_ACTIONS.FETCH_PLANS_FAILURE,
      payload: error.message || 'Failed to fetch plans'
    });
    throw error;
  }
};

// Fetch user's subscription
export const fetchMySubscription = () => async (dispatch) => {
  dispatch({ type: SUBSCRIPTION_ACTIONS.FETCH_SUBSCRIPTION_REQUEST });
  
  try {
    const response = await SubscriptionService.getMySubscription();
    dispatch({
      type: SUBSCRIPTION_ACTIONS.FETCH_SUBSCRIPTION_SUCCESS,
      payload: response
    });
    return response;
  } catch (error) {
    // Handle 401 gracefully - user is not authenticated
    if (error.response?.status === 401) {
      dispatch({
        type: SUBSCRIPTION_ACTIONS.FETCH_SUBSCRIPTION_SUCCESS,
        payload: { has_subscription: false, subscription: null }
      });
      return { has_subscription: false, subscription: null };
    }
    
    dispatch({
      type: SUBSCRIPTION_ACTIONS.FETCH_SUBSCRIPTION_FAILURE,
      payload: error.message || 'Failed to fetch subscription'
    });
    throw error;
  }
};

// Check subscription status
export const checkSubscriptionStatus = () => async (dispatch) => {
  dispatch({ type: SUBSCRIPTION_ACTIONS.CHECK_STATUS_REQUEST });
  
  try {
    const response = await SubscriptionService.checkStatus();
    dispatch({
      type: SUBSCRIPTION_ACTIONS.CHECK_STATUS_SUCCESS,
      payload: response
    });
    return response;
  } catch (error) {
    dispatch({
      type: SUBSCRIPTION_ACTIONS.CHECK_STATUS_FAILURE,
      payload: error.message || 'Failed to check subscription status'
    });
    throw error;
  }
};

// Fetch usage statistics
export const fetchUsage = () => async (dispatch) => {
  dispatch({ type: SUBSCRIPTION_ACTIONS.FETCH_USAGE_REQUEST });
  
  try {
    const response = await SubscriptionService.getUsage();
    dispatch({
      type: SUBSCRIPTION_ACTIONS.FETCH_USAGE_SUCCESS,
      payload: response
    });
    return response;
  } catch (error) {
    dispatch({
      type: SUBSCRIPTION_ACTIONS.FETCH_USAGE_FAILURE,
      payload: error.message || 'Failed to fetch usage statistics'
    });
    throw error;
  }
};

// Fetch payment history
export const fetchPayments = (limit = 50, offset = 0) => async (dispatch) => {
  dispatch({ type: SUBSCRIPTION_ACTIONS.FETCH_PAYMENTS_REQUEST });
  
  try {
    const response = await SubscriptionService.getPayments(limit, offset);
    dispatch({
      type: SUBSCRIPTION_ACTIONS.FETCH_PAYMENTS_SUCCESS,
      payload: response.payments
    });
    return response.payments;
  } catch (error) {
    dispatch({
      type: SUBSCRIPTION_ACTIONS.FETCH_PAYMENTS_FAILURE,
      payload: error.message || 'Failed to fetch payments'
    });
    throw error;
  }
};

// Create checkout session
export const createCheckoutSession = (planId, billingCycle) => async (dispatch) => {
  dispatch({ type: SUBSCRIPTION_ACTIONS.CREATE_CHECKOUT_REQUEST });
  
  try {
    const response = await SubscriptionService.createCheckoutSession(planId, billingCycle);
    dispatch({
      type: SUBSCRIPTION_ACTIONS.CREATE_CHECKOUT_SUCCESS,
      payload: response
    });
    return response;
  } catch (error) {
    dispatch({
      type: SUBSCRIPTION_ACTIONS.CREATE_CHECKOUT_FAILURE,
      payload: error.message || 'Failed to create checkout session'
    });
    throw error;
  }
};

// Create customer portal session
export const createPortalSession = () => async (dispatch) => {
  dispatch({ type: SUBSCRIPTION_ACTIONS.CREATE_PORTAL_REQUEST });
  
  try {
    const response = await SubscriptionService.createPortalSession();
    dispatch({
      type: SUBSCRIPTION_ACTIONS.CREATE_PORTAL_SUCCESS,
      payload: response
    });
    return response;
  } catch (error) {
    dispatch({
      type: SUBSCRIPTION_ACTIONS.CREATE_PORTAL_FAILURE,
      payload: error.message || 'Failed to create portal session'
    });
    throw error;
  }
};

// Cancel subscription
export const cancelSubscription = (atPeriodEnd = true) => async (dispatch) => {
  dispatch({ type: SUBSCRIPTION_ACTIONS.CANCEL_SUBSCRIPTION_REQUEST });
  
  try {
    const response = await SubscriptionService.cancelSubscription(atPeriodEnd);
    dispatch({
      type: SUBSCRIPTION_ACTIONS.CANCEL_SUBSCRIPTION_SUCCESS,
      payload: response
    });
    // Refresh subscription data after canceling
    dispatch(fetchMySubscription());
    return response;
  } catch (error) {
    dispatch({
      type: SUBSCRIPTION_ACTIONS.CANCEL_SUBSCRIPTION_FAILURE,
      payload: error.message || 'Failed to cancel subscription'
    });
    throw error;
  }
};

// Reactivate subscription
export const reactivateSubscription = () => async (dispatch) => {
  dispatch({ type: SUBSCRIPTION_ACTIONS.REACTIVATE_SUBSCRIPTION_REQUEST });
  
  try {
    const response = await SubscriptionService.reactivateSubscription();
    dispatch({
      type: SUBSCRIPTION_ACTIONS.REACTIVATE_SUBSCRIPTION_SUCCESS,
      payload: response
    });
    // Refresh subscription data after reactivating
    dispatch(fetchMySubscription());
    return response;
  } catch (error) {
    dispatch({
      type: SUBSCRIPTION_ACTIONS.REACTIVATE_SUBSCRIPTION_FAILURE,
      payload: error.message || 'Failed to reactivate subscription'
    });
    throw error;
  }
};

// Clear subscription errors
export const clearSubscriptionError = () => ({
  type: SUBSCRIPTION_ACTIONS.CLEAR_SUBSCRIPTION_ERROR
});

// Admin: Create new plan
export const createPlan = (planData) => async (dispatch) => {
  dispatch({ type: SUBSCRIPTION_ACTIONS.CREATE_PLAN_REQUEST });
  
  try {
    const response = await SubscriptionService.createPlan(planData);
    dispatch({
      type: SUBSCRIPTION_ACTIONS.CREATE_PLAN_SUCCESS,
      payload: response.plan
    });
    // Refresh plans list after creating
    dispatch(fetchPlans());
    return response;
  } catch (error) {
    dispatch({
      type: SUBSCRIPTION_ACTIONS.CREATE_PLAN_FAILURE,
      payload: error.message || 'Failed to create plan'
    });
    throw error;
  }
};

// Admin: Update plan
export const updatePlan = (planId, planData) => async (dispatch) => {
  dispatch({ type: SUBSCRIPTION_ACTIONS.UPDATE_PLAN_REQUEST });
  
  try {
    const response = await SubscriptionService.updatePlan(planId, planData);
    dispatch({
      type: SUBSCRIPTION_ACTIONS.UPDATE_PLAN_SUCCESS,
      payload: { planId, planData }
    });
    // Refresh plans list after updating
    dispatch(fetchPlans());
    return response;
  } catch (error) {
    dispatch({
      type: SUBSCRIPTION_ACTIONS.UPDATE_PLAN_FAILURE,
      payload: error.message || 'Failed to update plan'
    });
    throw error;
  }
};

// Admin: Delete plan
export const deletePlan = (planId) => async (dispatch) => {
  dispatch({ type: SUBSCRIPTION_ACTIONS.DELETE_PLAN_REQUEST });
  
  try {
    const response = await SubscriptionService.deletePlan(planId);
    dispatch({
      type: SUBSCRIPTION_ACTIONS.DELETE_PLAN_SUCCESS,
      payload: planId
    });
    // Refresh plans list after deleting
    dispatch(fetchPlans());
    return response;
  } catch (error) {
    dispatch({
      type: SUBSCRIPTION_ACTIONS.DELETE_PLAN_FAILURE,
      payload: error.message || 'Failed to delete plan'
    });
    throw error;
  }
};
